import {
  Component,
  EventEmitter,
  Input,
  Output,
  ElementRef,
  ViewChild,
  HostListener,
  AfterViewInit,
  OnChanges, SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-color-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './color-picker.component.html',
  styleUrls: ['./color-picker.component.scss']
})
export class ColorPickerComponent implements AfterViewInit, OnChanges {
  @Input() initialColor: string = '#ff0000';
  @Output() colorSelected = new EventEmitter<string>();

  @ViewChild('colorPickerModal') colorPickerModal!: ElementRef<HTMLDivElement>;
  @ViewChild('hueSlider') hueSlider!: ElementRef<HTMLCanvasElement>;
  @ViewChild('saturationSlider') saturationSlider!: ElementRef<HTMLCanvasElement>;
  @ViewChild('lightnessSlider') lightnessSlider!: ElementRef<HTMLCanvasElement>;

  showModal = false;
  currentColor: string = this.initialColor;
  hexInput: string = this.initialColor;
  selectedHue: number = 0;
  selectedSaturation: number = 100;
  selectedLightness: number = 50;

  isDraggingHue = false;
  isDraggingSaturation = false;
  isDraggingLightness = false;

  ngAfterViewInit() {
    this.currentColor = this.initialColor;
    this.hexInput = this.initialColor;
    this.updateColorFromHex(this.initialColor);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialColor'] && changes['initialColor'].currentValue) {
      const newColor = changes['initialColor'].currentValue;
      this.currentColor = newColor;
      this.hexInput = newColor;
      this.updateColorFromHex(newColor);
    }
  }

  onHexInputChange(){
    if (this.isValidHex(this.hexInput)) {
      this.currentColor = this.hexInput;
      this.updateColorFromHex(this.hexInput);
      this.colorSelected.emit(this.currentColor);
    }
  }

  isValidHex(hex: string) {
    return /^#[0-9A-F]{6}$/i.test(hex) || /^#[0-9A-F]{3}$/i.test(hex);
  }

  openModal(): void {
    this.showModal = true;
    setTimeout(() => {
      this.drawHueSlider();
      this.drawSaturationSlider();
      this.drawLightnessSlider();
    });
  }

  drawLightnessSlider(): void {
    const canvas = this.lightnessSlider.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    const gradient = ctx.createLinearGradient(0, 0, width, 0);
    gradient.addColorStop(0, '#000000'); // Черный
    gradient.addColorStop(0.5, `hsl(${this.selectedHue}, ${this.selectedSaturation}%, 50%)`); // Текущий цвет
    gradient.addColorStop(1, '#ffffff'); // Белый

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  }

  onLightnessSliderMouseDown(event: MouseEvent): void {
    this.isDraggingLightness = true;
    this.handleLightnessSliderMove(event);
  }

  onLightnessSliderClick(event: MouseEvent): void {
    this.handleLightnessSliderMove(event);
  }

  handleLightnessSliderMove(event: MouseEvent): void {
    const canvas = this.lightnessSlider.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const width = canvas.width;

    let lightness = Math.round((x / width) * 100);
    lightness = Math.max(0, Math.min(100, lightness));

    this.selectedLightness = lightness;
    this.updateCurrentColor();
  }

  closeModal(): void {
    this.showModal = false;
  }

  saveColor(): void {
    this.colorSelected.emit(this.currentColor);
    this.closeModal();
  }

  drawHueSlider(): void {
    const canvas = this.hueSlider.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    const gradient = ctx.createLinearGradient(0, 0, width, 0);
    gradient.addColorStop(0, '#FF0000');
    gradient.addColorStop(0.17, '#FFFF00');
    gradient.addColorStop(0.34, '#00FF00');
    gradient.addColorStop(0.51, '#00FFFF');
    gradient.addColorStop(0.68, '#0000FF');
    gradient.addColorStop(0.85, '#FF00FF');
    gradient.addColorStop(1, '#FF0000');

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  }

  drawSaturationSlider(): void {
    const canvas = this.saturationSlider.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    // Теперь градиент горизонтальный
    const gradient = ctx.createLinearGradient(0, 0, width, 0);
    gradient.addColorStop(0, `hsl(${this.selectedHue}, 100%, 50%)`);
    gradient.addColorStop(1, `hsl(${this.selectedHue}, 0%, 50%)`);

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  }

  onHueSliderMouseDown(event: MouseEvent): void {
    this.isDraggingHue = true;
    this.handleHueSliderMove(event);
  }

  onSaturationSliderMouseDown(event: MouseEvent): void {
    this.isDraggingSaturation = true;
    this.handleSaturationSliderMove(event);
  }

  onHueSliderClick(event: MouseEvent): void {
    this.handleHueSliderMove(event);
  }

  onSaturationSliderClick(event: MouseEvent): void {
    this.handleSaturationSliderMove(event);
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (this.isDraggingHue && event.buttons === 1) {
      this.handleHueSliderMove(event);
    }
    if (this.isDraggingSaturation && event.buttons === 1) {
      this.handleSaturationSliderMove(event);
    }
    if (this.isDraggingLightness && event.buttons === 1) {
      this.handleLightnessSliderMove(event);
    }
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.isDraggingHue = false;
    this.isDraggingSaturation = false;
    this.isDraggingLightness = false;
  }

  handleHueSliderMove(event: MouseEvent): void {
    const canvas = this.hueSlider.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const width = canvas.width;

    let hue = Math.round((x / width) * 360);
    hue = Math.max(0, Math.min(360, hue));

    this.selectedHue = hue;
    this.updateCurrentColor();
    this.drawSaturationSlider();
  }

  handleSaturationSliderMove(event: MouseEvent): void {
    const canvas = this.saturationSlider.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const width = canvas.width;

    let saturation = Math.round((x / width) * 100);
    saturation = Math.max(0, Math.min(100, saturation));

    this.selectedSaturation = saturation;
    this.updateCurrentColor();
  }

  updateCurrentColor(): void {
    this.currentColor = this.hslToHex(
      this.selectedHue,
      this.selectedSaturation,
      this.selectedLightness
    );
    this.drawSaturationSlider();
    this.drawLightnessSlider();
    this.setSliderMarkerPosition();
  }

  setSliderMarkerPosition(): void {
    document.documentElement.style.setProperty('--hue-position', `${this.selectedHue / 3.6}%`);
    document.documentElement.style.setProperty('--saturation-position', `${this.selectedSaturation}%`);
    document.documentElement.style.setProperty('--lightness-position', `${this.selectedLightness}%`);
  }

  private updateColorFromHex(hex: string): void {
    const hsl = this.hexToHsl(hex);
    this.selectedHue = hsl.h;
    this.selectedSaturation = hsl.s;
    this.selectedLightness = hsl.l;
  }

  private hexToHsl(hex: string): { h: number; s: number; l: number } {
    let r = parseInt(hex.substring(1, 3), 16) / 255;
    let g = parseInt(hex.substring(3, 5), 16) / 255;
    let b = parseInt(hex.substring(5, 7), 16) / 255;

    let max = Math.max(r, g, b);
    let min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      let d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }

      h! /= 6;
    }

    return {
      h: Math.round(h! * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  }

  private hslToHex(h: number, s: number, l: number): string {
    h /= 360;
    s /= 100;
    l /= 100;

    let r, g, b;

    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }

    const toHex = (x: number) => {
      const hex = Math.round(x * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }
}
